import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { PhoneNumberEditModal } from '@pages/Profile/PhoneNumbers/modals/PhoneNumberEdit.modal'

import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body2, Caption1 } from '@ui/Typography'
import { KebabIcon } from '@ui/icons/Kebab.icon'

import { ClientPhoneNumberDto } from '@model/AccountDetailedDto'

import { ScrollToIds, useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { PhoneNumberStatus } from './PhoneNumberStatus'

import styles from './PhoneNumbersCard.module.scss'

interface PhoneNumbersTableProps {
  phoneNumbers: ClientPhoneNumberDto[]
  deleteNumber(number: ClientPhoneNumberDto): void
  verifyNumber(number: ClientPhoneNumberDto): void
  makeNumberPrimary(number: ClientPhoneNumberDto): void
}

export const PhoneNumbersCard: React.FC<PhoneNumbersTableProps> = (props) => {
  const { phoneNumbers, deleteNumber, makeNumberPrimary } = props

  const { t } = useTranslation()
  const { scrollIntoView } = useScrollToElementIds()

  const editModal = useModal(PhoneNumberEditModal)

  const openEdit = (phoneNumber: ClientPhoneNumberDto) => {
    const handleDeleteById = (id: string) => {
      const pn = phoneNumbers.find((x) => x.id === id)
      if (pn) deleteNumber(pn)
    }
    const handleMakePrimaryById = (id: string) => {
      const pn = phoneNumbers.find((x) => x.id === id)
      if (pn) makeNumberPrimary(pn)
    }
    editModal.open({
      phoneNumber,
      deleteNumber: handleDeleteById,
      makeNumberPrimary: handleMakePrimaryById,
      onClose: () => scrollIntoView([ScrollToIds.ProfilePhoneNumbersHeader]),
    })
  }

  const primaryNumbers = useMemo(
    () => phoneNumbers.filter((phoneNumber) => phoneNumber.isPrimary),
    [phoneNumbers]
  )
  const secondaryNumbers = useMemo(
    () => phoneNumbers.filter((phoneNumber) => !phoneNumber.isPrimary),
    [phoneNumbers]
  )

  return (
    <React.Fragment>
      {!!primaryNumbers.length && (
        <>
          <Caption1 isParagraph className={styles.label}>
            {t('PhoneNumbers.Primary phone')}
          </Caption1>
          {primaryNumbers.map((phoneNumber) => (
            <PhoneCardBody
              {...props}
              phoneNumber={phoneNumber}
              onEdit={openEdit}
              key={phoneNumber.id}
            />
          ))}
        </>
      )}
      {!!secondaryNumbers.length && (
        <>
          <Caption1 isParagraph className={styles.label}>
            {t('PhoneNumbers.Secondary phone')}
          </Caption1>
          {secondaryNumbers.map((phoneNumber) => (
            <PhoneCardBody
              {...props}
              allowEdit
              phoneNumber={phoneNumber}
              onEdit={openEdit}
              key={phoneNumber.id}
            />
          ))}
        </>
      )}
    </React.Fragment>
  )
}

interface PhoneCardBodyProps {
  phoneNumber: ClientPhoneNumberDto
  allowEdit?: boolean
  onEdit(number: ClientPhoneNumberDto): void
}

const PhoneCardBody = (props: PhoneCardBodyProps & PhoneNumbersTableProps) => {
  const { phoneNumber, allowEdit, verifyNumber, onEdit } = props
  const { t } = useTranslation()

  return (
    <div className={styles.card}>
      <div className={styles.title}>
        <div className={styles.phoneWrapper}>
          <Body2 medium>
            <SensitiveData>{`${phoneNumber.countryCode} ${phoneNumber.number}`}</SensitiveData>
          </Body2>
        </div>
        {phoneNumber.isUsedIn2FA && (
          <Body2 isParagraph className={styles.twoFA} medium>
            <span>{t('PhoneNumbers.2FA')}</span>
          </Body2>
        )}
        <div className={styles.actionWrapper}>
          <span className={styles.verify}>
            <PhoneNumberStatus
              shouldVerify={phoneNumber.shouldVerify}
              verify={() => verifyNumber(phoneNumber)}
              isVerified={phoneNumber.isVerified}
            />
          </span>
          {allowEdit && (
            <span onClick={() => onEdit(phoneNumber)} className={styles.dots}>
              <KebabIcon />
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
